import type { AudioClip, Project } from '@/domain/types';
import { isAudioClip } from '@/domain/types';
import { effectivePlaybackRate } from '@/domain/selectors';
import {
  MAX_STRETCH_RATIO,
  MIN_STRETCH_RATIO,
  STRETCH_EPSILON,
  stretchChannels,
  stretchChannelsMapped,
} from './stretch';
import {
  isFormantShifted,
  isPitchShifted,
  pitchShiftChannels,
  quantiseSemitones,
} from './pitchShift';
import { planWarp, spanProgress, warpKey, type WarpPlan } from './warp';
import type { StretchRequest, StretchResponse } from './stretch.worker';

/**
 * Cache of pitch-preserved renders.
 *
 * A stretched clip does not play its asset at an altered rate — that would move
 * the pitch. It plays a *different buffer*: the whole asset, stretched once to
 * the ratio the clip needs, then read at rate 1. Two consequences fall out of
 * stretching the whole asset rather than the clip's own region:
 *
 * - trimming, splitting and moving a stretched clip cost nothing, because the
 *   region is still just an offset into a buffer;
 * - two clips of the same asset at the same ratio share one render.
 *
 * The cost is that a long asset backing a short clip is stretched in full. That
 * is the right trade while editing, where ratios are adjusted repeatedly, and it
 * is bounded: the cache is keyed by asset and ratio, and ratios are quantised.
 *
 * Renders are kept as raw channel data rather than as AudioBuffer, because the
 * live engine and each offline render own different contexts; every consumer
 * wraps the same samples in a buffer of its own.
 */

export interface StretchedAudio {
  channels: Float32Array[];
  sampleRate: number;
}

/**
 * Somewhere a render can be kept between sessions.
 *
 * Declared here, narrowly, rather than taken as a `StorageProvider`: the engine
 * needs two calls, and depending on the whole storage interface for them would
 * make the audio engine care about projects, recovery snapshots and quotas.
 * `IndexedDBStorage` satisfies this structurally, and so would anything else.
 *
 * Both calls are allowed to fail and are allowed to decline. A persistent cache
 * is an optimisation on top of a cache that already works, so nothing here may
 * turn a storage problem into a playback problem.
 */
export interface RenderStore {
  getRender(key: string): Promise<StretchedAudio | null>;
  putRender(key: string, assetId: string, render: StretchedAudio): Promise<void>;
}

/**
 * Ratios are rounded to four decimal places before they become a key. Dragging a
 * clip edge produces a continuum of ratios, and caching each one would stretch
 * the same asset dozens of times for differences no one can hear.
 */
export function quantiseRatio(ratio: number): number {
  return Math.round(Math.min(MAX_STRETCH_RATIO, Math.max(MIN_STRETCH_RATIO, ratio)) * 1e4) / 1e4;
}

/**
 * The stretch ratio a clip needs at a given playback rate, or null when it plays
 * its asset untouched. The rate is passed in rather than read off the clip: a
 * warped clip's rate comes from the project tempo, not from the clip.
 */
export function clipStretchRatio(clip: AudioClip, rate: number): number | null {
  if (!clip.preservePitch) return null;
  // A stretched buffer is read at rate 1, so it has to be the asset itself
  // squeezed or spread by the inverse of the rate.
  const ratio = quantiseRatio(1 / Math.max(0.01, rate));
  return Math.abs(ratio - 1) < STRETCH_EPSILON ? null : ratio;
}

/**
 * The suffixes that mark a *source transform* — something done to the asset's
 * samples before anything else reads them.
 *
 * There are two, and they compose. Reversal turns the audio round; a
 * transposition moves its pitch. Neither changes how long the audio is, so
 * either can be applied and the clip still occupies exactly the timeline it did
 * before. A stretched clip that is also reversed or transposed therefore
 * stretches the transformed audio rather than transforming a stretch, which is
 * the reading that means "play this backwards, a fifth up, slower".
 *
 * Making them part of the key rather than separate caches means a transformed
 * asset is rendered once for the whole project and evicted by the same rule as
 * everything else.
 */
const REVERSE_SUFFIX = '~rev';
const PITCH_SUFFIX = '~p';
/** Marks formants asked for somewhere other than where the pitch would take them. */
const FORMANT_SUFFIX = '~F';

/** The asset id a render reads, with the source transforms folded in. */
export function sourceKey(
  assetId: string,
  reversed: boolean,
  semitones = 0,
  formantSemitones: number | null = null,
): string {
  const shift = quantiseSemitones(semitones);
  const reverse = reversed ? REVERSE_SUFFIX : '';
  const pitch = isPitchShifted(shift) ? `${PITCH_SUFFIX}${shift}` : '';
  // Only a formant position that differs from where the pitch would take them
  // changes any samples, so only that reaches the key. Otherwise a clip could
  // split the cache into two identical halves by setting a field that has no
  // effect on it.
  const formants = isFormantShifted(shift, formantSemitones)
    ? `${FORMANT_SUFFIX}${quantiseSemitones(formantSemitones ?? 0)}`
    : '';
  return `${assetId}${reverse}${pitch}${formants}`;
}

/** Key under which a stretched render is cached, or the plain (possibly transformed) source. */
export function stretchKey(
  assetId: string,
  ratio: number | null,
  reversed = false,
  semitones = 0,
  formantSemitones: number | null = null,
): string {
  const source = sourceKey(assetId, reversed, semitones, formantSemitones);
  return ratio === null ? source : `${source}@${ratio}`;
}

/** A copy of the audio, back to front. */
export function reverseChannels(channels: readonly Float32Array[]): Float32Array[] {
  return channels.map((channel) => {
    const out = new Float32Array(channel.length);
    for (let i = 0, j = channel.length - 1; i < channel.length; i++, j--) out[i] = channel[j];
    return out;
  });
}

/** The ratio encoded in a buffer key, or null when the key names a plain asset. */
export function ratioFromKey(key: string): number | null {
  const at = key.lastIndexOf('@');
  if (at < 0) return null;
  const ratio = Number(key.slice(at + 1));
  return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
}

/**
 * What a clip needs rendered before it plays correctly.
 *
 * Four answers, and the order they are tried in is the order of how much work
 * they are:
 *
 *   none    it plays its asset as recorded, or as varispeed at a constant rate
 *   source  a transformed copy of the asset — reversed, transposed, or both —
 *           the same length as the asset and read at the clip's own rate
 *   ratio   a per-asset render at one length ratio, read at rate 1
 *   warp    a render of this one clip, bent span by span, read at rate 1
 *
 * `source` and `ratio` are not two unrelated things: a `ratio` render is a
 * `source` render that also changed length, which is why the length ratio hangs
 * off the source key rather than beside it.
 */
export type ClipRender =
  | { kind: 'none'; key: string }
  | {
      kind: 'source';
      key: string;
      assetId: string;
      reversed: boolean;
      semitones: number;
      formantSemitones: number | null;
    }
  | {
      kind: 'ratio';
      key: string;
      assetId: string;
      ratio: number;
      reversed: boolean;
      semitones: number;
      formantSemitones: number | null;
    }
  | {
      kind: 'warp';
      key: string;
      assetId: string;
      clipOffsetSec: number;
      plan: WarpPlan;
      preservePitch: boolean;
      reversed: boolean;
      semitones: number;
      formantSemitones: number | null;
    };

export function clipRender(project: Project, clip: AudioClip, rate: number): ClipRender {
  const semitones = quantiseSemitones(clip.pitchSemitones);
  // Only a formant position that differs from where the pitch would take them
  // changes any samples; anything else is the same render under another name.
  const formantSemitones = isFormantShifted(semitones, clip.formantSemitones) ? clip.formantSemitones : null;
  // Either moves the spectrum, and either therefore needs a render. Formants
  // move on their own, so a clip at its own pitch can still need one — which is
  // the whole reason the control is a position rather than a flag.
  const transformed = isPitchShifted(semitones) || formantSemitones !== null;
  const plan = planWarp(project, clip);
  if (plan) {
    // Reversed, the clip's span sits at the mirror position in the reversed
    // buffer, so that is where the warp render reads from. The bending itself is
    // unchanged: the audio arrives backwards and is then fitted to the grid,
    // which is what "play this backwards, in time" has to mean.
    const assetDuration = project.assets[clip.assetId]?.durationSec ?? clip.offsetSec + clip.durationSec;
    return {
      kind: 'warp',
      key: `${warpKey(clip, plan)}${clip.reversed ? REVERSE_SUFFIX : ''}${
        isPitchShifted(semitones) ? `${PITCH_SUFFIX}${semitones}` : ''
      }${formantSemitones !== null ? `${FORMANT_SUFFIX}${quantiseSemitones(formantSemitones)}` : ''}`,
      assetId: clip.assetId,
      clipOffsetSec: clip.reversed
        ? Math.max(0, assetDuration - clip.offsetSec - clip.durationSec)
        : clip.offsetSec,
      plan,
      preservePitch: clip.preservePitch,
      reversed: clip.reversed,
      semitones,
      formantSemitones,
    };
  }
  const ratio = clipStretchRatio(clip, rate);
  if (ratio === null) {
    // No length change to make. Either the clip plays its asset untouched, or it
    // plays a transformed copy of it at its own rate — which is what lets a
    // transposition sit on top of varispeed rather than replacing it.
    return clip.reversed || transformed
      ? {
          kind: 'source',
          key: sourceKey(clip.assetId, clip.reversed, semitones, formantSemitones),
          assetId: clip.assetId,
          reversed: clip.reversed,
          semitones,
          formantSemitones,
        }
      : { kind: 'none', key: clip.assetId };
  }
  return {
    kind: 'ratio',
    key: stretchKey(clip.assetId, ratio, clip.reversed, semitones, formantSemitones),
    assetId: clip.assetId,
    ratio,
    reversed: clip.reversed,
    semitones,
    formantSemitones,
  };
}

/**
 * Every render a project needs before it can play correctly, **in the order the
 * playhead reaches them**.
 *
 * The order is the whole reason this is sorted. Preparing a project is a queue
 * of expensive work, and if it runs in whatever order the clips happen to sit in
 * the map, the clip at bar one can be rendered last — so the first thing anybody
 * hears is the one thing that was not ready. Ticks are monotonic in time, so
 * sorting by them is sorting by when each render is first wanted.
 *
 * Two clips sharing a render take the earlier position, because the first
 * occurrence wins the deduplication.
 */
export function requiredRenders(project: Project): ClipRender[] {
  const clips = Object.values(project.clips)
    .filter(isAudioClip)
    .sort((a, b) => a.startTicks - b.startTicks);

  const seen = new Set<string>();
  const out: ClipRender[] = [];
  for (const clip of clips) {
    const render = clipRender(project, clip, effectivePlaybackRate(project, clip));
    if (render.kind === 'none' || seen.has(render.key)) continue;
    seen.add(render.key);
    out.push(render);
  }
  return out;
}

/**
 * What a project needs rendered, as one comparable string.
 *
 * Preparing is only worth starting when this changes. Every edit flows through
 * the store's commit path and most of them — moving a clip, drawing a fade,
 * renaming a track — do not touch a render at all.
 */
export function renderSignature(project: Project): string {
  return requiredRenders(project)
    .map((r) => r.key)
    .sort()
    .join('|');
}

/** The per-asset stretches a project needs, for callers that only handle those. */
export function requiredStretches(project: Project): { assetId: string; ratio: number }[] {
  return requiredRenders(project)
    .filter((r): r is Extract<ClipRender, { kind: 'ratio' }> => r.kind === 'ratio')
    .map((r) => ({ assetId: r.assetId, ratio: r.ratio }));
}

type BufferSource = (assetId: string) => Promise<AudioBuffer | null>;

export class StretchCache {
  private readonly renders = new Map<string, StretchedAudio>();
  private readonly inFlight = new Map<string, Promise<StretchedAudio | null>>();
  private readonly writes = new Set<Promise<void>>();
  private worker: Worker | null = null;
  private workerFailed = false;
  private nextRequestId = 1;
  private readonly pending = new Map<number, (response: StretchResponse) => void>();

  constructor(
    private readonly getBuffer: BufferSource,
    private readonly store: RenderStore | null = null,
  ) {}

  hasKey(key: string): boolean {
    return this.renders.has(key);
  }

  /**
   * A source-transformed copy of an asset — reversed, transposed, or both — the
   * same length as the asset and rendered on demand like a stretch.
   */
  async ensureSource(
    assetId: string,
    reversed: boolean,
    semitones: number,
    formantSemitones: number | null = null,
  ): Promise<StretchedAudio | null> {
    return this.coalesce(
      sourceKey(assetId, reversed, semitones, formantSemitones),
      assetId,
      () => this.render(assetId, null, reversed, semitones, formantSemitones),
      // A reversal on its own is a memcpy. Spending tens of megabytes of budget
      // to avoid one would fill the cache with its cheapest entries and evict
      // its most expensive — so only a shift makes this worth keeping.
      isPitchShifted(semitones),
    );
  }

  /** Renders if needed, coalescing duplicate requests for the same key. */
  async ensure(
    assetId: string,
    ratio: number,
    reversed = false,
    semitones = 0,
    formantSemitones: number | null = null,
  ): Promise<StretchedAudio | null> {
    return this.coalesce(
      stretchKey(assetId, ratio, reversed, semitones, formantSemitones),
      assetId,
      () => this.render(assetId, ratio, reversed, semitones, formantSemitones),
    );
  }

  /**
   * One render per key, however many callers ask for it — and the single place
   * the persistent store is consulted.
   *
   * The order is memory, then disk, then work. Playback and export both prepare
   * the same project, and a clip's render is often wanted again before the first
   * one has finished; without the in-flight map the same asset would go through
   * WSOLA twice and the second answer would overwrite an identical first.
   *
   * The write back to disk is deliberately **not** awaited. A render is wanted
   * because something is about to play, and making the first note wait on tens of
   * megabytes reaching IndexedDB would trade the problem this cache exists to
   * solve for a worse one. `flush()` is what waits, for callers that need to.
   */
  private async coalesce(
    key: string,
    assetId: string,
    work: () => Promise<StretchedAudio | null>,
    persistable = true,
  ): Promise<StretchedAudio | null> {
    const done = this.renders.get(key);
    if (done) return done;
    const running = this.inFlight.get(key);
    if (running) return running;

    const started = (async () => {
      if (persistable) {
        const stored = await this.store?.getRender(key).catch(() => null);
        if (stored) return stored;
      }
      const made = await work();
      if (made && persistable) this.persist(key, assetId, made);
      return made;
    })()
      .then((result) => {
        if (result) this.renders.set(key, result);
        return result;
      })
      .finally(() => this.inFlight.delete(key));
    this.inFlight.set(key, started);
    return started;
  }

  /** Writes a render back, in the background, never failing the render itself. */
  private persist(key: string, assetId: string, audio: StretchedAudio): void {
    if (!this.store) return;
    const write = this.store
      .putRender(key, assetId, audio)
      .catch(() => undefined)
      .finally(() => this.writes.delete(write));
    this.writes.add(write);
  }

  /**
   * Waits for every background write to finish.
   *
   * Nothing in playback needs this. Disposal uses it, so that renders made in
   * the last seconds of a session are on disk before the tab goes away, and
   * tests use it so "was it stored" is a question with an answer.
   */
  async flush(): Promise<void> {
    while (this.writes.size > 0) await Promise.all([...this.writes]);
  }

  /** Renders a clip that bends through a tempo change, keyed by its whole shape. */
  async ensureWarped(render: Extract<ClipRender, { kind: 'warp' }>): Promise<StretchedAudio | null> {
    return this.coalesce(render.key, render.assetId, async () => {
      const buffer = await this.getBuffer(render.assetId);
      if (!buffer) return null;
      const channels = render.reversed ? reverseChannels(channelsOf(buffer)) : channelsOf(buffer);
      return renderWarp(
        channels,
        buffer.sampleRate,
        render.clipOffsetSec,
        render.plan,
        render.preservePitch,
        render.semitones,
        render.formantSemitones,
      );
    });
  }

  /**
   * Renders everything a project needs, in the order the playhead reaches it.
   *
   * `signal` abandons the queue. An edit that changes what is needed supersedes
   * the run in flight, and finishing it would spend worker time and cache budget
   * on renders that are already known to be unwanted. Whatever was completed
   * before the abort is kept: it was correct when it was made, and the key it is
   * under says so.
   *
   * `onProgress` is called before each item, so a caller can say how far along
   * it is rather than only that it is busy.
   */
  async prepare(
    project: Project,
    options: { signal?: AbortSignal; onProgress?: (done: number, total: number) => void } = {},
  ): Promise<void> {
    const renders = requiredRenders(project);
    let done = 0;
    for (const render of renders) {
      if (options.signal?.aborted) return;
      options.onProgress?.(done++, renders.length);
      if (render.kind === 'ratio') {
        await this.ensure(
          render.assetId,
          render.ratio,
          render.reversed,
          render.semitones,
          render.formantSemitones,
        );
      } else if (render.kind === 'warp') await this.ensureWarped(render);
      else if (render.kind === 'source') {
        await this.ensureSource(
          render.assetId,
          render.reversed,
          render.semitones,
          render.formantSemitones,
        );
      }
    }
    options.onProgress?.(renders.length, renders.length);
  }

  /** Drops renders no clip needs any more. */
  evictUnused(project: Project): void {
    const keep = new Set(requiredRenders(project).map((r) => r.key));
    for (const key of [...this.renders.keys()]) {
      if (!keep.has(key)) this.renders.delete(key);
    }
  }

  /** A completed render by key, whatever kind it is. */
  getByKey(key: string): StretchedAudio | null {
    return this.renders.get(key) ?? null;
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.renders.clear();
    this.pending.clear();
  }

  /** How many renders are held in memory. Used by the status bar and by tests. */
  get size(): number {
    return this.renders.size;
  }

  /**
   * One render, whatever combination of transforms it needs.
   *
   * `ratio` is null for a source transform that leaves the length alone. The
   * order is fixed, and it is the whole model: reverse the samples, then shift
   * and stretch them in a single pass. Reversing afterwards would play a stretch
   * backwards rather than stretching backwards audio, and shifting in a pass of
   * its own would put the audio through WSOLA twice for nothing.
   */
  private async render(
    assetId: string,
    ratio: number | null,
    reversed: boolean,
    semitones: number,
    formantSemitones: number | null,
  ): Promise<StretchedAudio | null> {
    const buffer = await this.getBuffer(assetId);
    if (!buffer) return null;

    const channels = reversed ? reverseChannels(channelsOf(buffer)) : channelsOf(buffer);
    // A reversal on its own is the copy that has already been made.
    if (ratio === null && !isPitchShifted(semitones)) {
      return { channels, sampleRate: buffer.sampleRate };
    }

    const timeRatio = ratio ?? 1;
    const viaWorker = await this.renderInWorker(
      channels,
      buffer.sampleRate,
      timeRatio,
      semitones,
      formantSemitones,
    );
    if (viaWorker) return viaWorker;
    return {
      channels: isPitchShifted(semitones)
        ? pitchShiftChannels(channels, buffer.sampleRate, semitones, timeRatio, { formantSemitones })
        : stretchChannels(channels, buffer.sampleRate, timeRatio),
      sampleRate: buffer.sampleRate,
    };
  }

  private renderInWorker(
    channels: Float32Array[],
    sampleRate: number,
    ratio: number,
    semitones: number,
    formantSemitones: number | null,
  ): Promise<StretchedAudio | null> {
    const worker = this.ensureWorker();
    if (!worker) return Promise.resolve(null);

    const requestId = this.nextRequestId++;
    return new Promise<StretchedAudio | null>((resolve) => {
      this.pending.set(requestId, (response) => {
        resolve(response.channels ? { channels: response.channels, sampleRate } : null);
      });
      const request: StretchRequest = { requestId, channels, sampleRate, ratio, semitones, formantSemitones };
      worker.postMessage(
        request,
        channels.map((c) => c.buffer),
      );
    });
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (this.workerFailed) return null;
    try {
      this.worker = new Worker(new URL('./stretch.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (event: MessageEvent<StretchResponse>) => {
        const handler = this.pending.get(event.data.requestId);
        this.pending.delete(event.data.requestId);
        handler?.(event.data);
      };
      this.worker.onerror = () => {
        this.worker?.terminate();
        this.worker = null;
        // One failure is enough: falling back to the main thread is correct, and
        // retrying a broken worker on every clip would be worse than not having
        // one at all.
        this.workerFailed = true;
        for (const [requestId, handler] of this.pending) handler({ requestId, channels: null });
        this.pending.clear();
      };
    } catch {
      this.workerFailed = true;
      this.worker = null;
    }
    return this.worker;
  }
}

/** Wraps cached samples in a buffer belonging to a particular context. */
export function toAudioBuffer(ctx: BaseAudioContext, audio: StretchedAudio): AudioBuffer {
  const length = Math.max(1, audio.channels[0]?.length ?? 0);
  const buffer = ctx.createBuffer(Math.max(1, audio.channels.length), length, audio.sampleRate);
  for (let c = 0; c < audio.channels.length; c++) {
    // copyToChannel wants a view over a plain ArrayBuffer; cached channels may
    // have come back from a worker over one that TypeScript types as shared.
    const source = audio.channels[c];
    const view = new Float32Array(new ArrayBuffer(source.length * 4));
    view.set(source);
    buffer.copyToChannel(view, c);
  }
  return buffer;
}

/**
 * Renders a clip that bends through a tempo change.
 *
 * The whole clip goes through one stretching pass whose read position follows
 * the plan, rather than each span being rendered and the results butted
 * together. That matters: separate renders each start their overlap-add from
 * their own phase, so every boundary steps the waveform, and over a ramp — which
 * is subdivided into many spans — those steps accumulate into something that
 * measures as noise even though each span is individually clean.
 *
 * The plan's spans become a piecewise-linear map from output time to source
 * time. Positions are read from the plan's absolute times rather than
 * accumulated, so rounding cannot drift: the last sample lands exactly where the
 * tempo map says the clip ends.
 */
export function renderWarp(
  channels: readonly Float32Array[],
  sampleRate: number,
  clipOffsetSec: number,
  plan: WarpPlan,
  preservePitch: boolean,
  semitones = 0,
  formantSemitones: number | null = null,
): StretchedAudio {
  const totalFrames = Math.max(1, Math.round(plan.totalSec * sampleRate));
  const offsetFrames = clipOffsetSec * sampleRate;

  // Output sample -> source sample, piecewise-linear across the spans.
  const inputAt = (outSample: number): number => {
    const outSec = outSample / sampleRate;
    let span = plan.spans[plan.spans.length - 1];
    for (const candidate of plan.spans) {
      if (outSec < candidate.outEndSec) {
        span = candidate;
        break;
      }
    }
    const progress = spanProgress(span, outSec - span.outStartSec);
    const sourceSec = span.sourceStartSec + (span.sourceEndSec - span.sourceStartSec) * progress;
    return offsetFrames + sourceSec * sampleRate;
  };

  const rendered = preservePitch
    ? stretchChannelsMapped(channels, sampleRate, totalFrames, inputAt)
    : resampleMapped(channels, totalFrames, inputAt);

  // The shift is applied to the bent result rather than folded into the map. A
  // warp render is already this clip's own audio in timeline seconds, and the
  // shift is length-preserving, so a second pass over it leaves those seconds
  // exactly where the tempo map put them.
  return {
    channels: isPitchShifted(semitones)
      ? pitchShiftChannels(rendered, sampleRate, semitones, 1, { formantSemitones })
      : rendered,
    sampleRate,
  };
}

/** Varispeed along the same map: read the source at a moving rate. */
function resampleMapped(
  channels: readonly Float32Array[],
  outLength: number,
  inputAt: (outSample: number) => number,
): Float32Array[] {
  return channels.map((channel) => {
    const out = new Float32Array(outLength);
    for (let i = 0; i < outLength; i++) {
      const x = inputAt(i);
      const i0 = Math.floor(x);
      if (i0 < 0 || i0 >= channel.length) continue;
      const i1 = Math.min(channel.length - 1, i0 + 1);
      const t = x - i0;
      out[i] = channel[i0] * (1 - t) + channel[i1] * t;
    }
    return out;
  });
}

/**
 * The asset's samples, copied.
 *
 * Copied because the stretch worker *transfers* what it is given, and detaching
 * a decoded asset would leave the rest of the engine holding an empty buffer.
 */
function channelsOf(buffer: AudioBuffer): Float32Array[] {
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(new Float32Array(buffer.getChannelData(c)));
  return channels;
}
